import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Must match API_BASE in src/api/client.ts. Production traffic goes through the
// charity's own API domain (route api.lifespark360.com/v1/* on mustardtree-api).
const API_BASE = import.meta.env.PROD
  ? 'https://api.lifespark360.com/v1'
  : '/v1';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (data: { name: string; email: string; password: string }) => Promise<boolean>;
  logout: () => void;
  refresh: () => Promise<boolean>;
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      setAuth: (user, accessToken, refreshToken) => {
        set({ user, accessToken, refreshToken, isAuthenticated: true });
      },

      login: async (email, password) => {
        try {
          const res = await fetch(`${API_BASE}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });
          const data = await res.json();
          if (data.success) {
            set({
              user: data.data.user,
              accessToken: data.data.tokens.access_token,
              refreshToken: data.data.tokens.refresh_token,
              isAuthenticated: true,
            });
            return true;
          }
          return false;
        } catch {
          return false;
        }
      },

      register: async (input) => {
        try {
          const res = await fetch(`${API_BASE}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input),
          });
          const data = await res.json();
          if (data.success) {
            set({
              user: data.data.user,
              accessToken: data.data.tokens.access_token,
              refreshToken: data.data.tokens.refresh_token,
              isAuthenticated: true,
            });
            return true;
          }
          return false;
        } catch {
          return false;
        }
      },

      logout: () => {
        const { refreshToken, accessToken } = get();
        if (refreshToken && accessToken) {
          fetch(`${API_BASE}/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ refresh_token: refreshToken }),
          }).catch(() => {});
        }
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },

      refresh: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return false;
        try {
          const res = await fetch(`${API_BASE}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });
          const data = await res.json();
          if (data.success) {
            set({
              accessToken: data.data.tokens.access_token,
              refreshToken: data.data.tokens.refresh_token,
            });
            return true;
          }
          set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false });
          return false;
        } catch {
          return false;
        }
      },
    }),
    {
      name: 'mustardtree-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
